import type { FC, ReactNode } from 'react'
import type { Tone } from '@/types'

import { useRef, useState } from 'react'
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'motion/react'
import { cn } from '@/lib/utils'
import { EASE_OUT_EXPO } from '@/components/motion/reveal'
import { RollText } from '@/components/motion/roll-text'
import { guarantees } from '@/constants/content'

const TONES: Tone[] = ['violet', 'ink', 'lilac', 'void']

// Four promises, one at a time. The section is pinned while the visitor
// scrolls; each guarantee rolls its figure in and moves the page to a new tone.
export const Guarantees: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [index, setIndex] = useState(0)
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] })

    useMotionValueEvent(scrollYProgress, 'change', value => {
        setIndex(Math.min(guarantees.length - 1, Math.max(0, Math.floor(value * guarantees.length))))
    })

    const current = guarantees[index]

    return (
        <section id='guarantees' aria-labelledby='guarantees-title' className='relative'>
            <div ref={ref} className='relative h-[400vh]'>
                {TONES.map((tone, position) => (
                    <div
                        key={tone}
                        aria-hidden
                        data-section-tone={tone}
                        className='pointer-events-none absolute inset-x-0 h-[75vh]'
                        style={{ top: `${50 + position * 75}vh` }}
                    />
                ))}

                <ul className='sr-only'>
                    {guarantees.map(item => (
                        <li key={item.index}>
                            {item.title}: {item.text}
                        </li>
                    ))}
                </ul>

                <div aria-hidden className='sticky top-0 flex h-svh flex-col justify-between overflow-hidden px-5 pb-8 pt-24 md:px-8 lg:px-12 lg:pb-12 lg:pt-28'>
                    <div className='flex items-start justify-between gap-6 t-label text-tone-muted'>
                        <h2 id='guarantees-title'>
                            (03) What we guarantee
                        </h2>
                        <span className='tabular-nums'>
                            {current.index} / 0{guarantees.length}
                        </span>
                    </div>

                    <div className='relative flex flex-col'>
                        <AnimatePresence mode='wait'>
                            <motion.span
                                key={current.qualifier}
                                className='t-label mb-2 block text-tone-muted md:mb-4'
                                initial={{ opacity: 0, x: -12 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 12 }}
                                transition={{ duration: 0.4 }}
                            >
                                {current.qualifier}
                            </motion.span>
                        </AnimatePresence>

                        <RollText
                            value={current.value}
                            stagger={0.06}
                            className='font-heading text-[clamp(6.5rem,min(25vw,46svh),26rem)] font-semibold leading-[0.8] tracking-[-0.055em] wdth-62'
                        />
                    </div>

                    <div className='grid gap-8 md:grid-cols-12 md:items-end'>
                        <div className='md:col-span-7 lg:col-span-6'>
                            <AnimatePresence mode='wait'>
                                <motion.div
                                    key={current.title}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -16 }}
                                    transition={{ duration: 0.6, ease: EASE_OUT_EXPO }}
                                >
                                    <p className='font-heading text-3xl font-semibold uppercase tracking-tight wdth-87 md:text-5xl'>
                                        {current.title}
                                    </p>
                                    <p className='t-lead mt-3 max-w-lg text-tone-muted'>
                                        {current.text}
                                    </p>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        <ol className='grid grid-cols-4 gap-2 md:col-span-5 md:col-start-8 lg:col-span-4 lg:col-start-9'>
                            {guarantees.map((item, position) => (
                                <li key={item.index} className='flex flex-col gap-2'>
                                    <span className='h-0.5 overflow-hidden bg-tone-line'>
                                        <span className={cn('block h-full origin-left bg-tone transition-transform duration-700 ease-out-expo', position <= index ? 'scale-x-100' : 'scale-x-0')} />
                                    </span>
                                    <span className={cn('t-label transition-opacity duration-500', position === index ? 'opacity-100' : 'opacity-40')}>
                                        {item.title}
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </div>
        </section>
    )
}
